import acceptLanguageParser from "accept-language-parser";
import { z } from "zod";
import de from "$/modules/i18n/asset/messages/de.json";
import en from "$/modules/i18n/asset/messages/en.json";

export const localizationSchema = z.object({
  locale: z.union([z.literal("de"), z.literal("en")]),
});

export type Locale = z.infer<typeof localizationSchema>["locale"];

export const getLocale = (): Locale => {
  const supportedLanguages: Locale[] = ["de", "en"];
  const defaultLanguage = supportedLanguages[0];

  return (
    acceptLanguageParser.pick(supportedLanguages, defaultLanguage) ||
    defaultLanguage
  );
};

export const getMessages = async (data: z.infer<typeof localizationSchema>) => {
  const parsedData = localizationSchema.parse(data);

  const locale = {
    de,
    en,
  } as const;

  return locale[parsedData.locale];
};

function flattenObject(
  // biome-ignore lint/suspicious/noExplicitAny: Hier mache ich eine Ausnahme, da ich ein generisches Objekt flattenen möchte
  obj: Record<string, any>,
  prefix = "",
): Record<string, string> {
  const result: Record<string, string> = {};
  for (const key of Object.keys(obj)) {
    const pre = prefix ? `${prefix}.` : "";
    const value = obj[key];
    if (value && typeof value === "object" && !Array.isArray(value)) {
      const nested = flattenObject(value, pre + key);
      for (const nestedKey in nested) {
        result[nestedKey] = nested[nestedKey];
      }
    } else {
      result[pre + key] = String(value);
    }
  }
  return result;
}
// Typen für die Message-Struktur extrahieren
type Messages = typeof de; // oder typeof en, beide sollten identisch sein

type NestedKeyOf<ObjectType extends object> = {
  [Key in keyof ObjectType & (string | number)]: ObjectType[Key] extends object
    ? `${Key}` | `${Key}.${NestedKeyOf<ObjectType[Key]>}`
    : `${Key}`;
}[keyof ObjectType & (string | number)];

// Alle verfügbaren Namespace-Pfade
type NamespacePaths = NestedKeyOf<Messages>;

export const getTranslations = async (namespace: NamespacePaths) => {
  const locale = getLocale();
  const messages = await getMessages({ locale });

  const flattenMessages = flattenObject(messages);

  return (key: string): string => {
    const fullKey = namespace ? `${namespace}.${key}` : key;
    return flattenMessages[fullKey] || fullKey;
  };
};
